import { useState } from 'react';
import { RiLockPasswordLine, RiShieldKeyholeLine, RiAlertLine } from 'react-icons/ri';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../state/api';
import { setAuthToken } from '../state/api';
import { useStore } from '../state/store';
import { useAuthStore } from '../state/authStore';
import { cn } from '@/lib/utils';

/** 修改本人登录口令。改密会撤销该账号的全部旧会话，并签发新令牌。 */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useStore(state => state.toast);
  const refreshUser = useAuthStore(state => state.refresh);

  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength =
    form.next.length === 0
      ? null
      : form.next.length < 8
        ? '口令至少 8 位'
        : !/[a-zA-Z]/.test(form.next)
          ? '口令需包含字母'
          : !/[0-9]/.test(form.next)
            ? '口令需包含数字'
            : null;

  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;

  const submit = async () => {
    setError(null);
    if (strength) {
      setError(strength);
      return;
    }
    if (form.next !== form.confirm) {
      setError('两次输入的新口令不一致');
      return;
    }
    setBusy(true);
    try {
      const { token } = await api.changePassword({ current: form.current, next: form.next });
      // 旧会话已全部撤销，保存新令牌以保持当前登录
      setAuthToken(token);
      await refreshUser();
      toast('success', '口令已更新，其它设备的登录已失效');
      setForm({ current: '', next: '', confirm: '' });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className="w-[min(460px,95vw)]">
        <DialogHeader>
          <DialogTitle>修改我的口令</DialogTitle>
          <DialogDescription>
            修改成功后，该账号在其它设备的登录会立即失效，当前会话自动续用。
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>当前口令</Label>
            <div className="relative">
              <RiShieldKeyholeLine className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                type="password"
                autoComplete="current-password"
                value={form.current}
                onChange={e => {
                  setError(null);
                  setForm({ ...form, current: e.target.value });
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>新口令</Label>
            <div className="relative">
              <RiLockPasswordLine className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                type="password"
                autoComplete="new-password"
                placeholder="至少 8 位，含字母与数字"
                value={form.next}
                onChange={e => {
                  setError(null);
                  setForm({ ...form, next: e.target.value });
                }}
              />
            </div>
            {form.next.length > 0 && (
              <span className={cn('text-[10.5px]', strength ? 'text-[var(--color-cord)]' : 'text-[var(--color-evidence)]')}>
                {strength ?? '口令强度符合要求'}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>确认新口令</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={e => {
                setError(null);
                setForm({ ...form, confirm: e.target.value });
              }}
              onKeyDown={e => e.key === 'Enter' && submit()}
            />
            {mismatch && <span className="text-[10.5px] text-[var(--color-cord)]">两次输入不一致</span>}
          </div>

          {error && (
            <div className="flex items-start gap-1.5 rounded-[var(--radius-sm)] border border-[var(--color-cord)]/30 bg-[var(--color-cord)]/6 px-2.5 py-2 text-[11.5px] text-[var(--color-cord)]">
              <RiAlertLine className="mt-0.5 size-3.5 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={busy || !form.current || strength !== null || form.next !== form.confirm || !form.next}
            onClick={submit}
          >
            {busy ? '提交中…' : '更新口令'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}